import { HStack, Spacer, Tooltip, VStack } from '@chakra-ui/react';
import classNames from 'classnames';
import type { Series, SeriesPoint } from 'd3';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';

import tooltipStyles from '../../utilities/chart-tooltip.module.css';
import {
  PlotTooltip,
  PlotTooltipPortalProvider,
  useTooltipController,
} from '../plot-tooltip';
import type {
  buildSegmentEntries,
  StackedBarDataIndexed,
} from './stacked-bar-shared';
import styles from './stacked-bar-tooltip.module.css';

type StackedBarTooltipProps = PropsWithChildren & {
  title: string;
  titleRight?: string;
  entries?: {
    label: string;
    active: boolean;
    mainValue: string;
    secondaryValue?: string;
  }[];
};

export const StackedBarTooltipContent = ({
  title,
  titleRight,
  entries,
}: Omit<StackedBarTooltipProps, 'children'>) => (
  <VStack
    spacing={0}
    align="stretch"
    px="7px"
    className={styles.barTooltipBody}
  >
    <HStack justify="space-between" align="baseline" spacing={4}>
      <div className={styles.barTooltipLabel}>{title}</div>
      {titleRight && (
        <div className={styles.barTooltipTitleRight}>{titleRight}</div>
      )}
    </HStack>
    {entries === undefined && (
      <div className={styles.barTooltipValueNoData}>No Data</div>
    )}
    {entries?.map((entry, index) => {
      return (
        <HStack
          key={index}
          spacing={4}
          height="19px"
          className={styles.barTooltipRow}
        >
          <div
            className={classNames(styles.barTooltipValue, {
              [styles.highlight as string]: entry.active,
            })}
          >
            {entry.label}
          </div>
          <Spacer />
          <div
            className={classNames(styles.barTooltipValue, {
              [styles.highlight as string]: entry.active,
            })}
          >
            {entry.secondaryValue
              ? `${entry.mainValue} / ${entry.secondaryValue}`
              : entry.mainValue}
          </div>
        </HStack>
      );
    })}
  </VStack>
);

export const StackedBarTooltip = ({
  title,
  titleRight,
  entries,
  children,
}: StackedBarTooltipProps) => {
  return (
    <Tooltip
      hasArrow
      padding="12px"
      placement="top"
      borderRadius="4px"
      className={tooltipStyles.surface}
      label={
        <StackedBarTooltipContent
          title={title}
          titleRight={titleRight}
          entries={entries}
        />
      }
    >
      {children}
    </Tooltip>
  );
};

type SeriesData = Series<StackedBarDataIndexed, string>;
type Segment = SeriesPoint<StackedBarDataIndexed>;

export type StackedBarTooltipState = {
  key: string;
  svgX: number;
  svgY: number;
  title: string;
  entries: ReturnType<typeof buildSegmentEntries> | undefined;
};

export const findNearestBarIndex = (
  starts: readonly (number | undefined)[],
  thickness: number,
  coord: number
): number | null => {
  const n = starts.length;
  if (n === 0) return null;
  const centerAt = (i: number): number | undefined => {
    const start = starts[i];
    return start === undefined ? undefined : start + thickness / 2;
  };
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const center = centerAt(mid);
    if (center === undefined || center < coord) lo = mid + 1;
    else hi = mid;
  }
  const distAt = (i: number): number => {
    const center = centerAt(i);
    return center === undefined
      ? Number.POSITIVE_INFINITY
      : Math.abs(center - coord);
  };
  if (lo > 0 && distAt(lo - 1) <= distAt(lo)) return lo - 1;
  return lo;
};

export const findSegmentAtValue = (
  stackedSeries: readonly SeriesData[],
  index: number,
  scale: (value: number) => number,
  coord: number
): { series: SeriesData; segment: Segment } | null => {
  for (const series of stackedSeries) {
    const segment = series[index];
    if (!segment) continue;
    const a = scale(segment[0]);
    const b = scale(segment[1]);
    if (
      Math.abs(a - b) > 0 &&
      coord >= Math.min(a, b) &&
      coord <= Math.max(a, b)
    ) {
      return { series, segment };
    }
  }
  for (let k = stackedSeries.length - 1; k >= 0; k--) {
    const series = stackedSeries[k];
    const segment = series?.[index];
    if (
      series &&
      segment &&
      Math.abs(scale(segment[0]) - scale(segment[1])) > 0
    ) {
      return { series, segment };
    }
  }
  return null;
};

export const useStackedBarTooltip = (
  hitTest: (pointer: [number, number]) => StackedBarTooltipState | null
) => {
  const [state, setState] = useState<StackedBarTooltipState | null>(null);
  const keyRef = useRef<string | null>(null);

  const { isVisible, gProps } = useTooltipController({
    onHover: (pointer) => {
      const next = hitTest(pointer);
      if (next === null || keyRef.current === next.key) return;
      keyRef.current = next.key;
      setState(next);
    },
  });

  useEffect(() => {
    if (!isVisible) {
      keyRef.current = null;
      setState(null);
    }
  }, [isVisible]);

  return { state, isVisible, gProps };
};

export const StackedBarSharedTooltip = ({
  state,
  isVisible,
}: {
  state: StackedBarTooltipState | null;
  isVisible: boolean;
}) => {
  if (!state) return null;
  return (
    <PlotTooltipPortalProvider>
      <PlotTooltip isVisible={isVisible} x={state.svgX} y={state.svgY}>
        <StackedBarTooltipContent title={state.title} entries={state.entries} />
      </PlotTooltip>
    </PlotTooltipPortalProvider>
  );
};
